// base
import { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

// hooks
import { useResetRecoil } from './useResetRecoil';

// modlues
import { CurrentMenuAtom, useGetUsersId } from 'modules';
import { useAppDispatch, useAppSelector } from 'modules/store';
import { UserIdAtom, UserInfoAtom, UserRolesAtom } from 'modules/user/atoms/user.atom';

// routes
import { ROUTE_SIGN_IN } from 'routes/const';

// enums
import { UserRole } from 'enums';

// services
import { COOKIE_ACCESS_TOKEN, cookieStorage } from 'services';

// libraries
import { useRecoilValue } from 'recoil';

export const useUser = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();

  const { temporaryPasswdStatus } = useAppSelector((state) => state.auth);

  const userId = useRecoilValue(UserIdAtom);
  const userInfo = useRecoilValue(UserInfoAtom);
  const userRole = useRecoilValue(UserRolesAtom);
  const currentMenu = useRecoilValue(CurrentMenuAtom);

  const { onResetAllRecoil } = useResetRecoil();

  useGetUsersId({
    data: {
      userId: userId as string
    },
    options: {
      enabled: !!userId
    }
  });

  const isLogin = useMemo(() => {
    return !!userId && !!userInfo;
  }, [userId, userInfo]);

  const isMasterAdmin = useMemo(() => {
    if (!userInfo) return false;

    return userInfo.role === UserRole.ROLE_MASTER_ADMIN;
  }, [userInfo]);

  const isSiteAdmin = useMemo(() => {
    if (!userInfo) return false;

    return userInfo.role === UserRole.ROLE_SITE_ADMIN;
  }, [userInfo]);

  const isConstructor = useMemo(() => {
    if (!userInfo) return false;

    return userInfo.role === UserRole.ROLE_CONSTRUCTOR;
  }, [userInfo]);

  const isSubConstructor = useMemo(() => {
    if (!userInfo) return false;

    return userInfo.role === UserRole.ROLE_SUB_CONSTRUCTOR;
  }, [userInfo]);

  const isWorker = useMemo(() => {
    if (!userInfo) return false;

    return userInfo.role === UserRole.ROLE_WORKER;
  }, [userInfo]);

  const actionRoles = useMemo(() => {
    if (!currentMenu) {
      return {
        isCreate: false,
        isRead: false,
        isUpdate: false,
        isDelete: false,
        isSearch: false
      };
    }

    const { createYn, deleteYn, readYn, updateYn, searchYn } = currentMenu;

    return {
      isCreate: createYn === 'Y',
      isRead: readYn === 'Y',
      isUpdate: updateYn === 'Y',
      isDelete: deleteYn === 'Y',
      isSearch: searchYn === 'Y'
    };
  }, [currentMenu]);

  const onLogout = useCallback(
    (callback?: () => void) => {
      window.localStorage.clear();
      sessionStorage.clear();
      onResetAllRecoil();

      if (callback) {
        callback();
      }

      history.replace(ROUTE_SIGN_IN);
    },
    [history, onResetAllRecoil]
  );

  useEffect(() => {
    const accessToken = cookieStorage.getCookie(COOKIE_ACCESS_TOKEN);

    if (isLogin && accessToken) return;

    // dispatch(authActions.setLogout());
  }, [dispatch, isLogin]);

  return {
    isLogin,
    userInfo,
    userRole,
    temporaryPasswdStatus,
    isMasterAdmin,
    isSiteAdmin,
    isConstructor,
    isSubConstructor,
    isWorker,
    currentMenu,
    onLogout,
    userId,
    ...actionRoles
  };
};
